
import React, { useState, useEffect } from 'react';
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface GenerationStatusIndicatorProps {
  isGeneratingTitle: boolean;
  isGeneratingReport: boolean;
  lastTitleGenerated?: Date;
  lastReportGenerated?: Date;
  titleError?: boolean;
  reportError?: boolean;
  progressiveSections?: string[];
  showProgressiveDisplay?: boolean;
  onProgressiveDisplayComplete?: () => void;
}

export const GenerationStatusIndicator: React.FC<GenerationStatusIndicatorProps> = ({
  isGeneratingTitle,
  isGeneratingReport,
  lastTitleGenerated,
  lastReportGenerated,
  titleError,
  reportError,
  progressiveSections = [],
  showProgressiveDisplay = false,
  onProgressiveDisplayComplete
}) => {
  const { t } = useTranslation();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(-1);
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  const [displayedSection, setDisplayedSection] = useState<string | null>(null);

  const isGenerating = isGeneratingTitle || isGeneratingReport;
  const hasError = titleError || reportError;

  // Handle progressive sections display
  useEffect(() => {
    if (!showProgressiveDisplay || progressiveSections.length === 0) {
      setCurrentSectionIndex(-1);
      setCompletedSections([]);
      setDisplayedSection(null);
      return;
    }

    // Start the progressive display
    setCurrentSectionIndex(0);
    setCompletedSections([]);
    setDisplayedSection(progressiveSections[0]);

    const interval = setInterval(() => {
      setCurrentSectionIndex(prev => {
        if (prev < progressiveSections.length - 1) {
          const nextIndex = prev + 1;
          setCompletedSections(prevCompleted => [...prevCompleted, progressiveSections[prev]]);
          setDisplayedSection(progressiveSections[nextIndex]);
          return nextIndex;
        } else {
          // Mark last section as completed
          setCompletedSections(prevCompleted => [...prevCompleted, progressiveSections[prev]]);
          setDisplayedSection(null);
          clearInterval(interval);
          setTimeout(() => {
            onProgressiveDisplayComplete?.();
          }, 1000); // Give time to show the last section completion
          return prev;
        }
      });
    }, 1500); // Show each section for 1.5 seconds

    return () => clearInterval(interval);
  }, [progressiveSections, showProgressiveDisplay, onProgressiveDisplayComplete]);

  // If showing progressive display, show current section being updated
  if (showProgressiveDisplay && displayedSection) {
    return (
      <div className="flex items-center space-x-2">
        <div className="flex items-center space-x-1">
          <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />
          <span className="text-xs text-blue-700 font-medium">
            {t('updating_section', { section: displayedSection })}
          </span>
        </div>
      </div>
    );
  }

  // If progressive display just completed, show completion message briefly
  if (showProgressiveDisplay && !displayedSection && completedSections.length > 0) {
    return (
      <div className="flex items-center space-x-2">
        <div className="flex items-center space-x-1">
          <Check className="w-3 h-3 text-green-600" />
          <span className="text-xs text-green-700 font-medium">
            {t('report_updated_completely')}
          </span>
        </div>
      </div>
    );
  }

  // Default status indicator
  return (
    <div className="flex items-center space-x-2">
      <div className="flex items-center space-x-1">
        {/* Status indicator circle */}
        <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
          isGenerating ? 'bg-blue-500 animate-pulse' :
          hasError ? 'bg-red-500' :
          (lastTitleGenerated || lastReportGenerated) ? 'bg-green-500' : 'bg-gray-300'
        }`} />
        
        {/* Status text */}
        <span className="text-xs text-gray-600">
          {isGeneratingTitle ? t('generating_title') :
           isGeneratingReport ? t('generating_report') :
           hasError ? t('generation_error') :
           (lastTitleGenerated || lastReportGenerated) ? t('generation_complete') : t('ai_standby')}
        </span>
        
        {/* Success/Error icon */}
        {!isGenerating && (lastTitleGenerated || lastReportGenerated) && !hasError && (
          <Check className="w-3 h-3 text-green-600" />
        )}
        {!isGenerating && hasError && (
          <AlertCircle className="w-3 h-3 text-red-600" />
        )}
      </div>
    </div>
  );
};
